import React from 'react';

import { EchoNavigate, EchoSlot, type EchoSnapshot, useEcho, useEchoSnapshot } from '@betterment/js-runtime';
import { Banner, Box, H1, LegacySectionLayout } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';

import { Notifications } from '../notifications/Notifications';
import { WithLayout } from '../RetailApp/components/WithLayout';
import FullPageLoader from '../Shared/components/FullPageLoader';

function PerformanceRealtimeLoaderContent({ snapshot }: { snapshot: EchoSnapshot }) {
  const { echo, isPreview, onSubmit } = useEcho(snapshot);

  // Don't render previews, since revalidation causes the chart animation to replay.
  if (isPreview) {
    return <FullPageLoader />;
  }

  if (echo.type === 'navigation') {
    return <EchoNavigate echo={echo} fallback={<FullPageLoader />} />;
  }

  return <EchoSlot slot={echo.slots.main!} onSubmit={onSubmit} />;
}

const PerformanceRealtimeLoaderApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const url = `/app/performance_realtime?${searchParams.toString()}`;
  const result = useEchoSnapshot(url);

  return (
    <WithLayout parts={{ sidebar: true, footer: true }}>
      <Helmet>
        <title>Performance | Betterment</title>
      </Helmet>

      <Notifications />

      <LegacySectionLayout css={{ py: 'space.400', bg: 'background.surface' }}>
        <LegacySectionLayout.ContentWrapper>
          <H1>Performance</H1>
        </LegacySectionLayout.ContentWrapper>
      </LegacySectionLayout>

      <section className="sc-SectionLayout sc-p-h--0 sc-p-h-md--m ft-performanceCards">
        <div className="sc-SectionLayout-contentWrapper">
          {result.status === 'loading' && <FullPageLoader />}
          {result.status === 'ok' && <PerformanceRealtimeLoaderContent snapshot={result.snapshot} />}
          {result.status === 'error' && (
            <Box css={{ height: '90vh' }}>
              <Banner variant="critical">
                An error occurred loading your performance data. Please try again later or reach out to support for
                assistance.
              </Banner>
            </Box>
          )}
        </div>
      </section>
    </WithLayout>
  );
};

export default PerformanceRealtimeLoaderApp;
